import { FaArrowRight, FaCheck, FaMoneyBill, FaCalculator } from "react-icons/fa";
import { Button } from "../../../../app/components";
import { useState, useEffect } from "react";
import { CustomModal } from "../../../Common/Modal";
import { Reserva } from "../../../../types";
import { Form, Row, Col, Alert, InputGroup } from "react-bootstrap";
import { formatPrice } from "../../../../utils/priceUtils";
import { updateReserva } from "../../../../services/firestoreService";
import { showToast } from "../../../../utils/toast";
import { useConfirmModal } from "../../../../hooks/useConfirmModal";
import ConfirmModal from "../../../Common/ConfirmModal";

interface RegisterWithdrawalProps {
   reserva: Reserva;
   onStatusChange?: () => void;
   onClose?: () => void;
}

interface WithdrawalPaymentData {
   saldoDevedor: number;
   formaPagamento: string;
   valorPago: number;
   desconto: number;
   observacoes?: string;
   valorEntrada?: number;
}

export const RegisterWithdrawal = ({ reserva, onStatusChange, onClose }: RegisterWithdrawalProps) => {
   const [show, setShow] = useState(false);
   const [loading, setLoading] = useState(false);
   const { modalState, showConfirm, hideConfirm, handleConfirm } = useConfirmModal();

   // Calcular saldo devedor correto considerando desconto
   const saldoDevedorCorreto = reserva.pagamentosData?.saldoDevedor || 0;
   const valorEntradaPaga = reserva.pagamentosData?.valorEntrada || 0;

   const [paymentData, setPaymentData] = useState<WithdrawalPaymentData>({
      saldoDevedor: saldoDevedorCorreto,
      formaPagamento: '',
      valorPago: saldoDevedorCorreto,
      desconto: 0,
      observacoes: '',
      valorEntrada: valorEntradaPaga
   });


   const formasPagamento = [
      { value: 'dinheiro', label: 'Dinheiro' },
      { value: 'pix', label: 'PIX' },
      { value: 'cartao_debito', label: 'Cartão Débito' },
      { value: 'cartao_credito', label: 'Cartão Crédito' },
      { value: 'transferencia', label: 'Transferência' }
   ];

   // Inicializar valores corretos quando o modal abrir
   useEffect(() => {
      if (show) {
         setPaymentData({
            saldoDevedor: saldoDevedorCorreto,
            formaPagamento: '',
            valorPago: saldoDevedorCorreto,
            desconto: 0,
            observacoes: '',
            valorEntrada: valorEntradaPaga
         });
      }
   }, [show, saldoDevedorCorreto, valorEntradaPaga]);

   // Calcular valores automaticamente quando desconto (%) mudar
   // Se saldo devedor = 0, não precisamos recalcular
   useEffect(() => {
      if (saldoDevedorCorreto === 0) return;

      const descontoValor = (saldoDevedorCorreto * (paymentData.desconto || 0)) / 100;
      const valorComDesconto = saldoDevedorCorreto - descontoValor;
      setPaymentData(prev => ({
         ...prev,
         valorPago: Math.max(0, valorComDesconto)
      }));
   }, [paymentData.desconto, saldoDevedorCorreto]);

   const handleInputChange = (field: keyof WithdrawalPaymentData, value: any) => {
      setPaymentData(prev => ({
         ...prev,
         [field]: value
      }));
   };

   const handleDescontoChange = (value: number) => {
      const percentual = Math.max(0, Math.min(value, 100));
      const descontoValor = (paymentData.saldoDevedor * percentual) / 100;
      const valorComDesconto = paymentData.saldoDevedor - descontoValor;

      setPaymentData(prev => ({
         ...prev,
         desconto: percentual,
         valorPago: Math.max(0, valorComDesconto)
      }));
   };

   const handleRegisterWithdrawal = () => {
      if (saldoDevedorCorreto > 0 && !paymentData.formaPagamento) {
         showToast.error('Selecione uma forma de pagamento');
         return;
      }

      // Se houver saldo devedor, o valor deve ser maior que zero
      if (saldoDevedorCorreto > 0 && paymentData.valorPago <= 0) {
         showToast.error('Valor pago deve ser maior que zero');
         return;
      }

      // Preparar mensagem de confirmação
      const mensagemValor = saldoDevedorCorreto > 0 
         ? `\nValor a pagar: ${formatPrice(paymentData.valorPago)}` 
         : `\n✓ Cliente já pagou o valor total na reserva`;

      // Mostrar modal de confirmação
      showConfirm(
         'Confirmar Retirada',
         `Deseja realmente registrar a retirada para o cliente ${reserva.clienteNome}?${mensagemValor}\nForma de pagamento: ${formasPagamento.find(f => f.value === paymentData.formaPagamento)?.label || paymentData.formaPagamento}\n\nEsta ação irá alterar o status da reserva para "Retirado".`,
         async () => {
               try {
                  setLoading(true);

               // Base de pagamentos da reserva (usar optional chaining para evitar erros de tipo)
               const pagamentos = reserva.pagamentosData;
               const descontoEscopo = pagamentos?.descontoEscopo || 'total';

               // Entrada realmente paga pelo cliente:
               // - Se desconto for na entrada, usar valorEntradaComDesconto
               // - Sempre somar a 2ª forma quando existir
               const entradaBase = descontoEscopo === 'entrada'
                  ? (pagamentos?.valorEntradaComDesconto ?? pagamentos?.valorEntrada ?? 0)
                  : (pagamentos?.valorEntrada ?? 0);
               const valorEntradaTotal = entradaBase + (pagamentos?.valorFormaPagamento2 ?? 0);

               // Valor pago nesta retirada (já com desconto de retirada aplicado no input)
               const valorRetirada = paymentData.valorPago;

               // Total pago acumulado (entrada + retirada)
               const novoValorPago = valorEntradaTotal + valorRetirada;

               // Valor líquido que precisava ser pago
               const valorLiquido = descontoEscopo === 'total'
                  ? (pagamentos?.valorComDesconto ?? pagamentos?.valorTotal ?? reserva.valorTotal ?? 0)
                  : (pagamentos?.valorTotal ?? reserva.valorTotal ?? 0);

               // Desconto aplicado na retirada para persistir
               const retiradaDescontoValor = (saldoDevedorCorreto * (paymentData.desconto || 0)) / 100;
               const retiradaDescontoPercentual = paymentData.desconto || 0;

               // Status considera valor pago + desconto concedido na retirada
               const novoStatusPagamento = (novoValorPago + (retiradaDescontoValor || 0)) >= valorLiquido ? 'pago' : 'parcial';

               // Atualizar reserva
               const updatedReserva = {
                  ...reserva,
                  status: 'retirado' as const,
                  statusPagamento: novoStatusPagamento as 'pago' | 'parcial',
                  valorPago: novoValorPago,
                  observacoes: paymentData.observacoes || reserva.observacoes,
                  retiradaDescontoPercentual,
                  retiradaDescontoValor
               };

               const result = await updateReserva(reserva.id!, updatedReserva);

               if (result.success) {
                  showToast.success('Retirada registrada com sucesso!');
                  setShow(false);
                  
                  // Fechar modal de detalhes da reserva
                  if (onClose) {
                     setTimeout(() => {
                        onClose();
                     }, 100);
                  }
                  
                  if (onStatusChange) {
                     onStatusChange();
                  }
               } else {
                  showToast.error(result.error || 'Erro ao registrar retirada');
                  setLoading(false);
               }
            } catch (error) {
               showToast.error('Erro ao registrar retirada');
               setLoading(false);
            }
         },
         'success',
         'Sim, Confirmar',
         'Cancelar'
      );
   };
   if (reserva.status !== 'reservado') return null;

   return (
      <div className="d-flex flex-column flex-sm-row gap-2">
         <Button
            btnColor="success"
            onClick={() => setShow(true)}
            extraCss="d-flex align-items-center gap-2"
            disabled={reserva.status !== 'reservado'}
         >
            <FaArrowRight />
            Registrar Retirada
         </Button>

         <CustomModal
            show={show}
            onHide={() => setShow(false)}
            size="xl"
            headerLabel="Registrar Retirada"
            footer={
               <div className="d-flex gap-2">
                  <Button btnColor="secondary" onClick={() => setShow(false)}>
                     Cancelar
                  </Button>
                  <Button
                     btnColor="success"
                     onClick={handleRegisterWithdrawal}
                     disabled={loading}
                  >
                     {loading ? (
                        <>
                           <span className="spinner-border spinner-border-sm me-2" role="status" />
                           Processando...
                        </>
                     ) : (
                        <>
                           <FaCheck className="me-2" />
                           Registrar Retirada
                        </>
                     )}
                  </Button>
               </div>
            }
         >
            <div className="row">
               <div className="col-12">
                  {/* Campos de Pagamento */}
                  <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                     <h6 className="mb-3">
                        <FaMoneyBill className="me-2" />
                        Pagamento da Retirada
                     </h6>

                     <Row>
                        {/* Valor da Entrada */}
                        {/* <Col md={6} className="mb-3">
                           <Form.Label>Valor da Entrada (Já Pago)</Form.Label>
                           <InputGroup>
                              <InputGroup.Text>R$</InputGroup.Text>
                              <Form.Control
                                 type="text"
                                 value={formatPrice(reserva.pagamentosData?.valorEntrada || 0)}
                                 readOnly
                                 className="bg-success text-white"
                              />
                           </InputGroup>
                           <Form.Text className="text-success">
                              ✓ Valor já recebido
                           </Form.Text>
                        </Col> */}

                        {/* Saldo Devedor */}
                        <Col md={4} className="mb-3">
                           <Form.Label>Saldo Devedor (Falta Pagar)</Form.Label>
                           <InputGroup>
                              <InputGroup.Text>R$</InputGroup.Text>
                              <Form.Control
                                 type="text"
                                 value={formatPrice(paymentData.saldoDevedor)}
                                 readOnly
                                 className={paymentData.saldoDevedor > 0 ? "bg-danger text-white" : "bg-success text-white"}
                              />
                           </InputGroup>
                           <Form.Text className={paymentData.saldoDevedor > 0 ? "text-danger" : "text-success"}>
                              {paymentData.saldoDevedor > 0 ? "Valor restante a pagar" : "✓ Cliente já pagou tudo"}
                           </Form.Text>
                        </Col>
                        {/* Desconto na Retirada (%) */}
                        <Col md={4} className="mb-3">
                           <Form.Label>Desconto (%)</Form.Label>
                           <InputGroup>
                              <InputGroup.Text>%</InputGroup.Text>
                              <Form.Control
                                 type="number"
                                 min={0}
                                 max={100}
                                 step={0.01}
                                 onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    handleDescontoChange(isNaN(v) ? 0 : v);
                                 }}
                                 placeholder="Percentual de desconto"
                              />
                           </InputGroup>
                        </Col>
                        {/* Valor a Pagar */}
                        <Col md={4} className="mb-3">
                           <Form.Label>Valor a Pagar</Form.Label>
                           <InputGroup>
                              <InputGroup.Text>R$</InputGroup.Text>
                              <Form.Control
                                 type="text"
                                 value={formatPrice(paymentData.valorPago)}
                                 readOnly
                                 className="bg-success text-white"
                              />
                           </InputGroup>
                           <Form.Text className={"text-success"}>
                              {paymentData.valorPago > 0 ? "Valor calculado automaticamente" : "✓ Cliente já pagou tudo na reserva"}
                           </Form.Text>
                        </Col>
                        {/* Forma de Pagamento */}
                        {
                           saldoDevedorCorreto > 0 && (
                        <Col md={4} className="mb-3">
                           <Form.Label>Forma de Pagamento *</Form.Label>
                           <Form.Select
                              value={paymentData.formaPagamento}
                              onChange={(e) => handleInputChange('formaPagamento', e.target.value)}
                              required
                           >
                              <option value="">Selecione...</option>
                              {formasPagamento.map((forma) => (
                                 <option key={forma.value} value={forma.value}>
                                    {forma.label}
                                 </option>
                              ))}
                           </Form.Select>
                        </Col>
                           )
                        }


                        {/* Observações */}
                        <Col md={12} className="mb-3">
                           <Form.Label>Observações</Form.Label>
                           <Form.Control
                              as="textarea"
                              rows={3}
                              value={paymentData.observacoes}
                              onChange={(e) => handleInputChange('observacoes', e.target.value)}
                              placeholder="Observações sobre a retirada..."
                           />
                        </Col>
                     </Row>
                  </div>
               </div>
            </div>
         </CustomModal>

         {/* Modal de Confirmação */}
         <ConfirmModal
            show={modalState.show}
            onHide={hideConfirm}
            onConfirm={handleConfirm}
            title={modalState.title}
            message={modalState.message}
            type={modalState.type}
            confirmText={modalState.confirmText}
            cancelText={modalState.cancelText}
            loading={loading}
         />
      </div>
   );
};